import React, { Component } from 'react';
import {
  Alert,
  Button,
  ButtonGroup,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';
import { connect } from 'react-redux';
import Moment from 'react-moment';
import { PacmanLoader } from 'react-spinners';

import XMModalConfirm from '../../components/XMModalConfirm';
import XMTable from '../../components/XMTable';
import XMPagination from '../../components/XMPagination';
import * as backupActions from '../../actions/backupActions';
import XMTooltipButton from '../../components/XMTooltipButton';
import { isDevelopment } from '../../utils/environment';
import _ from 'lodash';

const STATUS_COLOR = {
  // NONE and QUEUED reach the CDN column now that it renders in the clone dialog too. Without an entry each
  // one renders an unstyled button beside styled siblings.
  NONE: 'secondary',
  QUEUED: 'secondary',
  IN_PROGRESS: 'warning',
  TRANSFORMING: 'warning',
  ERROR: 'danger',
  DONE: 'success'
};

// Static copy of xmd.service.store-manager.backup.retention.{unpinnedMonths,pinnedMonths}; the service does not
// expose them, so keep these in step with platform-central-configuration.
const UNPINNED_RETENTION_MONTHS = 6;
const PINNED_RETENTION_MONTHS = 18;

@connect(state => {
  return {
    store: state.store,
    backup: state.backup
  };
})
class BackupTable extends Component {
  constructor(props) {
    super(props);

    // storeId is read from props, never mirrored here: this component is reused across stores, so a copy
    // taken in the constructor would still name the previously viewed store.
    this.state = {
      modalDelete: false,
      modalRestore: false,
      modalPin: false,
      modalUnpin: false,
      pinNote: '',
      pagination: {}
    };

    this.toggleDelete = this.toggleDelete.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.toggleRestore = this.toggleRestore.bind(this);
    this.handleRestore = this.handleRestore.bind(this);
    this.togglePin = this.togglePin.bind(this);
    this.handlePin = this.handlePin.bind(this);
    this.toggleUnpin = this.toggleUnpin.bind(this);
    this.handleUnpin = this.handleUnpin.bind(this);
  }

  toggleDelete(backupId) {
    this.setState({
      backupId: backupId,
      modalDelete: !this.state.modalDelete
    });
  }

  handleDelete() {
    const { dispatch, storeId } = this.props;
    dispatch(backupActions.remove(storeId, this.state.backupId));
    this.toggleDelete();
  }

  toggleRestore(backupId) {
    this.setState({
      backupId: backupId,
      modalRestore: !this.state.modalRestore
    });
  }

  // Pinning an already pinned backup replaces its note, so the box opens on the current one.
  togglePin(backupId) {
    const selected = this.props.all.find(b => b.id === backupId);
    this.setState({
      backupId: backupId,
      pinNote: (selected && selected.pinNote) || '',
      modalPin: !this.state.modalPin
    });
  }

  handlePin() {
    const { dispatch, storeId } = this.props;
    dispatch(backupActions.pin(storeId, this.state.backupId, this.state.pinNote));
    this.setState({ modalPin: false, pinNote: '' });
  }

  toggleUnpin(backupId) {
    this.setState({
      backupId: backupId,
      modalUnpin: !this.state.modalUnpin
    });
  }

  handleUnpin() {
    const { dispatch, storeId } = this.props;
    dispatch(backupActions.unpin(storeId, this.state.backupId));
    this.setState({ modalUnpin: false });
  }

  // The clone dialog lists another store's backups, so the row being acted on is not always in backup.all.
  selectedBackup() {
    const { backup, isCloneOnly } = this.props;
    const backupId = this.state.backupId;
    const rows = isCloneOnly ? backup.allCloneable.backups : backup.all;
    return _.find(rows, b => b.id === backupId);
  }

  handleRestore() {
    const { toggle, dispatch, storeId } = this.props;

    let backupSelected = this.selectedBackup();

    backupSelected = {
      ...backupSelected,
      store: backupSelected.storeResponse
    };

    dispatch(backupActions.restore(storeId, backupSelected));

    this.toggleRestore();
    if (toggle) {
      toggle();
    }
  }

  // The store a backup belongs to, against the store being restored into -- the same comparison the server
  // makes. Which table is on screen cannot stand in for it, in either direction: backup.all survives a store
  // change while the new store's rows load, so store B briefly renders store A's, and the clone picker does
  // not exclude the store you are standing on. An absent id means an older peer, and falls back to treating
  // the restore as a borrow, which is the more cautious of the two messages.
  isSelfRestore(selected) {
    // Ids are scoped to the environment that issued them, so store 42 in production and store 42 here are
    // different stores that compare equal. The clone dialog loads another environment's backups on purpose
    // (backupActions.getAllCloneableByStore), so there its ids cannot be measured against a local one at all.
    // Read from the rows, not from a prop: a request that resolves after the environment was switched
    // still lands its own rows, and a prop would then describe the wrong ones.
    if (this.props.isCloneOnly && this.props.backup.allCloneable.foreign) {
      return false;
    }
    const sourceId = (selected.storeResponse || {}).id;
    return sourceId != null && String(sourceId) === String(this.props.storeId);
  }

  restoreMessage() {
    const selected = this.selectedBackup();
    if (!selected || !selected.databaseOnly) {
      return 'Are you sure you want to RESTORE this Backup?';
    }
    const sourceStore = selected.storeResponse || {};
    // Restored onto itself: the server has an explicit branch for it (BackupService.borrowedMedia,
    // "Restored onto itself") and records no borrow, leaves the folder alone and never blanks the CDN
    // password. The borrow wording would name this store as the one it borrows from, which reads as a
    // contradiction, and would warn about uploads that keep working. The media is still worth a word,
    // because the database goes back in time and the media does not.
    if (!sourceStore.cdnSourceStoreCode && this.isSelfRestore(selected)) {
      return 'Are you sure you want to RESTORE this Backup? It holds no CDN media, so this store keeps the media it has now.';
    }
    // A source that is itself borrowing has nothing in its own folder, so the server follows the chain to
    // the store that holds the media. Naming the source here would promise images from the wrong store.
    const source = sourceStore.cdnSourceStoreCode || sourceStore.storeId || 'the source store';
    // "will be pointed at" rather than "will read": the server declines the borrow when the source is
    // deleted, absent here, or this store itself, and the browser cannot tell which of those applies.
    return `Are you sure you want to RESTORE this Backup? It holds no CDN media. This store will be pointed at ${source}'s media rather than getting its own, and CDN uploads into this store will not work.`;
  }

  deleteMessage() {
    const selected = this.selectedBackup();
    return selected && selected.databaseOnly
      ? 'Are you sure you want to DELETE this Backup? The database backup file is permanently removed and cannot be recovered.'
      : 'Are you sure you want to DELETE this Backup? The database and CDN backup files are permanently removed and cannot be recovered.';
  }

  isWaiting(statusType) {
    return statusType == 'IN_PROGRESS' || statusType == 'TRANSFORMING';
  }

  // Hovering a pinned backup shows why it is being kept, which is the whole point of the note.
  pinTooltip(b) {
    if (!b.pinned) {
      return 'Pin';
    }
    return b.pinNote ? `Pinned: ${b.pinNote}` : 'Pinned (no note) — click to unpin';
  }

  // Who pinned it and when, kept to the row's hover text so the note itself stays readable.
  pinnedByTitle(b) {
    if (!b.pinnedBy && !b.pinnedDate) {
      return undefined;
    }
    const when = b.pinnedDate ? new Date(b.pinnedDate).toLocaleString() : 'an unknown date';
    return `Pinned by ${b.pinnedBy || 'unknown'} on ${when}`;
  }

  // A database-only backup has no CDN half to wait for, so the restore button must not wait for one -- without
  // this it stays disabled for the whole life of the backup. The flag is what decides it, not the status: the
  // server records NONE for a database-only job, but NONE is also the model default every backup starts at, so
  // reading it would mean trusting that a full backup always leaves NONE before its database half reports DONE.
  // It does today, but that is the server's ordering to change, not ours to depend on. `== null` rather than
  // `=== null` because a peer environment on an older build omits the field entirely.
  hasNoCdnHalf(b) {
    return b.databaseOnly || b.cdnStatusType == null;
  }

  canRestore(b) {
    return b.statusType === 'DONE' && (this.hasNoCdnHalf(b) || b.cdnStatusType === 'DONE');
  }

  // Mirrors the statuses the server refuses to delete: a running job still owns its files.
  isRunning(b) {
    const running = s => s == 'QUEUED' || s == 'IN_PROGRESS' || s == 'TRANSFORMING';
    return running(b.statusType) || running(b.cdnStatusType);
  }

  handleCancelRestore = () => {
    this.setState({ modalRestore: false });
  };

  handleCancelDelete = () => {
    this.setState({ modalDelete: false });
  };

  handleCancelPin = () => {
    this.setState({ modalPin: false, pinNote: '' });
  };

  handleCancelUnpin = () => {
    this.setState({ modalUnpin: false });
  };

  handleDismissError = () => {
    this.props.dispatch(backupActions.clearError());
  };

  render() {
    const { all, pagination, paginate, backup, isCloneOnly = false } = this.props;

    let headers = [];
    headers.push({ name: 'File Name' });
    headers.push({ name: 'Type' });
    // The clone dialog lists another store's finished backups, so their DB status tells the reader nothing.
    // Whether a backup carries CDN media does: restoring a database-only one leaves this store reading the
    // source store's folder, and without this column the two kinds of row look identical.
    if (!isCloneOnly) {
      headers.push({ name: 'DB' });
    }
    headers.push({ name: 'CDN' });
    headers.push({ name: '' });
    headers.push({ name: 'Actions' });

    return all.length == 0 ? (
      <div style={{ display: 'flex', justifyContent: 'center' }}>No backups</div>
    ) : (
      <div>
        <XMModalConfirm
          isVisible={this.state.modalRestore}
          handleConfirm={this.handleRestore}
          onCancel={this.handleCancelRestore}
          message={this.restoreMessage()}
          isValidatingStoreUrl={!isDevelopment()}
        />

        <XMModalConfirm
          isVisible={this.state.modalDelete}
          handleConfirm={this.handleDelete}
          onCancel={this.handleCancelDelete}
          message={this.deleteMessage()}
        />

        <XMModalConfirm
          isVisible={this.state.modalUnpin}
          handleConfirm={this.handleUnpin}
          onCancel={this.handleCancelUnpin}
          message={`Are you sure you want to UNPIN this Backup? Its note will be removed and it will be deleted automatically ${UNPINNED_RETENTION_MONTHS} months after it was created instead of ${PINNED_RETENTION_MONTHS}.`}
        />

        <Modal isOpen={this.state.modalPin} toggle={this.handleCancelPin}>
          <ModalHeader toggle={this.handleCancelPin}>Pin Backup</ModalHeader>
          <ModalBody>
            <p>
              Pinning extends automatic deletion from {UNPINNED_RETENTION_MONTHS} to {PINNED_RETENTION_MONTHS} months
              after the backup was created. Pinned backups are not kept indefinitely.
            </p>
            <FormGroup>
              <Label htmlFor="pinNote">Note (optional) — why is this backup being kept?</Label>
              <Input
                type="textarea"
                name="pinNote"
                id="pinNote"
                rows="3"
                maxLength={1000}
                value={this.state.pinNote}
                onChange={e => this.setState({ pinNote: e.target.value })}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="secondary" className="me-2" onClick={this.handleCancelPin}>
              Cancel
            </Button>
            <Button type="button" color="primary" onClick={this.handlePin}>
              Pin
            </Button>
          </ModalFooter>
        </Modal>

        <Alert color="danger" isOpen={!!backup.error} toggle={this.handleDismissError}>
          {backup.error}
        </Alert>

        <XMTable headers={headers} autoRenderRow={true}>
          {all.map(b => (
            <React.Fragment key={b.id}>
              <td className="align-middle">{b.fileName}</td>
              <td className="align-middle">{b.type}</td>
              {!isCloneOnly ? (
                <td className="align-middle">
                  <Button color={STATUS_COLOR[b.statusType]} size="sm">
                    {this.isWaiting(b.statusType) ? (
                        <div style={{ display: 'inline-flex', alignItems: 'center', width: '10em' }}>
                        <span>{b.statusType}</span>
                        <div className="ms-2 pacman-loader-wrapper">
                          <PacmanLoader size={8.5} color={'#FFF'} loading={true} />
                        </div>
                      </div>
                    ) : (
                      <span>{b.statusType}</span>
                    )}
                  </Button>
                </td>
              ) : null}
              <td className="align-middle">
                {b.databaseOnly ? (
                  <span className="text-muted" title="No CDN media was backed up">DB only</span>
                ) : (
                  b.cdnStatusType !== null && (
                    <Button color={STATUS_COLOR[b.cdnStatusType]} size="sm">
                      {this.isWaiting(b.cdnStatusType) ? (
                        <div style={{ display: 'inline-flex', alignItems: 'center', width: '10em' }}>
                          <span>{b.cdnStatusType}</span>
                          <div className="ms-2 pacman-loader-wrapper">
                            <PacmanLoader size={8.5} color={'#FFF'} loading={true} />
                          </div>
                        </div>
                      ) : (
                        <span>{b.cdnStatusType}</span>
                      )}
                    </Button>
                  )
                )}
              </td>
              <td className="align-middle">
                <Container>
                  {b.cloneStoreName ? (
                    <Row>
                      <Col xs="5">
                        <span className="text-primary">Cloned From</span>
                      </Col>
                      <Col>
                        <span className="text-primary">{b.cloneStoreName}</span>
                      </Col>
                    </Row>
                  ) : null}
                  {b.pinned ? (
                    <Row title={this.pinnedByTitle(b)}>
                      <Col xs="5">
                        <span className="text-warning">Pinned</span>
                      </Col>
                      <Col>
                        <span className="text-warning">{b.pinNote || 'No note given'}</span>
                      </Col>
                    </Row>
                  ) : null}
                  <Row>
                    <Col xs="5">
                      <span className="text-secondary">Created By</span>
                    </Col>
                    <Col>
                      <span className="text-secondary">{b.createdBy}</span>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs="5">
                      <span className="text-secondary">Created Date</span>
                    </Col>
                    <Col>
                      <span className="text-secondary">
                        <Moment format="Do MMM YYYY, HH:mm" withTitle>
                          {b.createdDate}
                        </Moment>
                      </span>
                    </Col>
                  </Row>
                </Container>
              </td>
              <td className="align-middle">
                <ButtonGroup>
                  <XMTooltipButton
                    icon="icon-cloud-upload"
                    tooltipTitle="Restore"
                    onClick={() => this.toggleRestore(b.id)}
                    disabled={!this.canRestore(b)}
                  />
                  {!isCloneOnly ? (
                    <XMTooltipButton
                      icon={b.pinned ? 'fa-solid fa-thumbtack' : 'icon-pin'}
                      className={b.pinned ? 'btn-transparent text-warning' : 'btn-transparent'}
                      tooltipTitle={this.pinTooltip(b)}
                      onClick={() => (b.pinned ? this.toggleUnpin(b.id) : this.togglePin(b.id))}
                    />
                  ) : null}
                  {!isCloneOnly ? (
                    <XMTooltipButton
                      icon="icon-trash"
                      tooltipTitle={this.isRunning(b) ? 'Delete (unavailable while running)' : 'Delete'}
                      onClick={() => this.toggleDelete(b.id)}
                      disabled={this.isRunning(b)}
                    />
                  ) : null}
                </ButtonGroup>
              </td>
            </React.Fragment>
          ))}
        </XMTable>
        {paginate && pagination ? <XMPagination pagination={pagination} paginate={paginate} /> : null}
      </div>
    );
  }
}

export default BackupTable;
